<template>
  <div class="friction-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.3;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-fric"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <!-- 箭头定义 -->
              <marker id="arr-g" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                 <polygon points="0 0, 10 5, 0 10" fill="#f43f5e" />
              </marker>
              <marker id="arr-n" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                 <polygon points="0 0, 10 5, 0 10" fill="#10b981" />
              </marker>
              <marker id="arr-f" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                 <polygon points="0 0, 10 5, 0 10" fill="#facc15" />
              </marker>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 斜面座 -->
           <path :d="`M 100 500 L 700 500 L 100 ${500 - 600 * Math.tan(rad)} Z`" fill="#1e293b" stroke="#334155" stroke-width="4" />

           <!-- 物体及受力分析 -->
           <g :transform="`translate(${objX}, ${objY}) rotate(${-angle})`">
              <!-- 物体 -->
              <rect x="-40" y="-60" width="80" height="60" fill="#38bdf8" stroke="#fff" stroke-width="3" rx="4" filter="url(#glow-fric)" />
              
              <!-- 1. 重力 G (始终垂直向下) -->
              <g :transform="`rotate(${angle})`">
                 <line x1="0" y1="-30" x2="0" y2="120" stroke="#f43f5e" stroke-width="4" marker-end="url(#arr-g)" />
                 <text x="10" y="110" fill="#f43f5e" font-size="20" font-weight="900">G</text>
              </g>

              <!-- 2. 支持力 N (垂直斜面向上) -->
              <line x1="0" y1="-30" x2="0" y2="-150" stroke="#10b981" stroke-width="4" marker-end="url(#arr-n)" />
              <text x="10" y="-130" fill="#10b981" font-size="20" font-weight="900">N</text>

              <!-- 3. 摩擦力 f (沿斜面向上) -->
              <line x1="-40" y1="-2" :x2="-40 - frictionArrowLen" y2="-2" stroke="#facc15" stroke-width="4" marker-end="url(#arr-f)" />
              <text :x="-50 - frictionArrowLen" y="-20" fill="#facc15" font-size="20" font-weight="900">f</text>
           </g>

           <!-- 临界摩擦角指示线 -->
           <line v-if="showFrictionAngle" x1="100" y1="500" :x2="100 + 400" :y2="500 - 400 * mu" stroke="#facc15" stroke-width="2" stroke-dasharray="8,6" opacity="0.5" />
           <text v-if="showFrictionAngle" :x="520" :y="500 - 400 * mu" fill="#facc15" font-size="14">临界摩擦角 φ</text>

        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">力学平衡与临界分析</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">受力数值拆解 (G=100N)</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>斜面倾角 θ</span><span class="hl text-yellow">{{ angle }} °</span></div>
                  <div class="data-row"><span>摩擦系数 μ</span><span class="hl text-purple">{{ mu.toFixed(2) }}</span></div>
                  <div class="divider"></div>
                  <div class="data-row"><span>重力下滑分力 G_x</span><span class="hl text-red">{{ gx.toFixed(2) }} N</span></div>
                  <div class="data-row"><span>法向支持力 N</span><span class="hl text-green">{{ n.toFixed(2) }} N</span></div>
                  <div class="data-row" :style="isSliding ? 'background:rgba(244,63,94,0.1)' : 'background:rgba(250,204,21,0.1)'">
                     <span>{{ isSliding ? '滚动摩擦力' : '静摩擦力' }} f</span><span class="hl" :class="isSliding ? 'text-red' : 'text-yellow'">{{ currentFriction.toFixed(2) }} N</span>
                  </div>
                  <div class="data-row"><span>状态判定</span><span class="hl" :class="isSliding ? 'text-red' : 'text-green'">{{ isSliding ? '正在下滑' : '静止平衡' }}</span></div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item">
               <label>斜面倾角 θ ({{ angle }}°)</label>
               <el-slider v-model="angle" :min="0" :max="60" :step="1" size="small"></el-slider>
            </div>
            
            <div class="s-item">
               <label>表面摩擦系数 μ ({{ mu.toFixed(2) }})</label>
               <el-slider v-model="mu" :min="0.1" :max="1.2" :step="0.05" size="small"></el-slider>
            </div>

            <div class="s-item">
               <el-checkbox v-model="showFrictionAngle" style="margin-top: 10px;">显示临界摩擦角辅助线</el-checkbox>
            </div>

            <div class="s-item" style="display: flex; align-items: flex-end;">
               <el-button type="primary" size="mini" @click="resetObj" style="width: 100%;">重置物体位置</el-button>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FrictionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      angle: 20,
      mu: 0.5,
      showFrictionAngle: false,
      offset: 0,
      isRunning: true,
      lastTimestamp: 0
    }
  },
  mounted() {
    this.startLoop();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.timer);
  },
  computed: {
    rad() { return (this.angle * Math.PI) / 180; },
    gx() { return 100 * Math.sin(this.rad); },
    n() { return 100 * Math.cos(this.rad); },
    fMax() { return this.mu * this.n; },
    isSliding() { return this.gx > this.fMax; },
    currentFriction() {
       return this.isSliding ? this.fMax : this.gx;
    },
    frictionArrowLen() {
       return Math.min(this.currentFriction * 1.5, 150);
    },
    objX() {
       const baseD = 200 + this.offset;
       return 100 + baseD * Math.cos(this.rad);
    },
    objY() {
       const baseD = 200 + this.offset;
       return 500 - 600 * Math.tan(this.rad) + baseD * Math.sin(this.rad);
    },
    topicIntro() {
       return this.knowledgePoint.includes('摩擦角') ? '摩擦角：由法向支持力与最大静摩擦力合成的临界合力倾角。' : '摩擦力：阻碍物体相对运动或相对运动趋势的力。';
    },
    activeFormula() {
       return this.isSliding ? 'f = μN (滑动)' : 'f = G·sinθ (静摩擦)';
    },
    tipDesc() {
       if (this.isSliding) return '【滑动状态】此时重力沿斜面的分力超过了最大静摩擦力。物体的加速度是由 (Gx - f) 提供的。注意即使你继续增加倾角，由于 N 在减小，滑动摩擦力 f 反而会变小！';
       return '【静止状态】此时摩擦力的大小并不等于 μN，而是精准地等于重力的下滑分力 G·sinθ。它像一个“变色龙”，你给多少下滑力，它就产生多少阻力。直到倾角达到【摩擦角】θ = arctan(μ)，平衡将被打破。';
    }
  },
  methods: {
    resetObj() {
       this.offset = 0;
    },
    startLoop() {
       const tick = (now) => {
          if (this.lastTimestamp && this.isSliding) {
             const dt = (now - this.lastTimestamp) / 1000;
             const accel = (this.gx - this.fMax) * 0.5; // Scaled for vis
             this.offset += accel; 
             if (this.offset > 400) this.offset = -100;
          }
          this.lastTimestamp = now;
          this.timer = requestAnimationFrame(tick);
       };
       this.timer = requestAnimationFrame(tick);
    }
  }
}
</script>

<style scoped lang="scss">
.friction-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 380px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }
.divider { height: 1px; background: rgba(255,255,255,0.1); margin: 4px 0; }

.text-yellow { color: #facc15; } .text-green { color: #10b981; } .text-blue { color: #38bdf8; } .text-purple { color: #a855f7; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
